/**
 * Looking around without an account.
 *
 * The rule this implements: the app is gated, but the gate should not be
 * the first thing a stranger meets. Someone who has just been sent a link
 * and wants to know what this is should be able to read the schedule and
 * the lessons. Doing anything that produces or stores something, joining a
 * room, making a prediction, following a club, earning IQ, needs at least
 * an anonymous account.
 *
 * So browse mode grants READ of the public football content and nothing
 * else. It is not a weaker session: there is no uid, no profile and no
 * write path, which is exactly why every write already fails safely
 * without special-casing.
 *
 * `sessionStorage`, not `localStorage`, on purpose. Browsing is a decision
 * about this visit. A returning visitor should meet the landing page and
 * its offer again rather than silently resuming a mode they picked once
 * weeks ago and have forgotten about.
 */

const KEY = "idkf.browse";

/** Routes a browsing visitor may read. Everything else needs an account. */
export const BROWSABLE_ROUTE_KEYS = Object.freeze([
  "GAMES",
  "GAME",
  "LEARN",
  "CONCEPT",
  "TEAMS",
  "PLAYER",
]);

const listeners = new Set();

function readStored() {
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    /* Private mode, or storage disabled. Browsing simply will not stick. */
    return false;
  }
}

let browsing = typeof window === "undefined" ? false : readStored();

export function isBrowsing() {
  return browsing;
}

export function setBrowsing(next) {
  browsing = Boolean(next);
  try {
    if (browsing) window.sessionStorage.setItem(KEY, "1");
    else window.sessionStorage.removeItem(KEY);
  } catch {
    /* Keep the in-memory answer; it lasts as long as the tab. */
  }
  listeners.forEach((listener) => listener());
}

export function subscribeBrowsing(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test-only: forget the mode and every listener. */
export function __resetBrowsingForTests() {
  browsing = false;
  listeners.clear();
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
